import { Dialog, DialogContent, Stack, CircularProgress, Typography, FormControlLabel, Stepper, Step, StepLabel, Box, Alert } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createApplicant, resetCreateApplicant } from 'store/slices/employer/applicants/applicant-actions-slice';
import { RootState } from 'store';
import { ShButton, ShFileUploadButton, ShTextFieldV2 } from 'shared/SharedStyles/ShInputs';
import { UploadResumeIcon } from 'assets/Icons/UploadResume';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { Snackbar } from '@mui/material';
import { AppDispatch } from 'shared/SharedModels';
import { ShPaper } from 'shared/SharedStyles/ShSurfaces';
import { ShSwitch } from 'shared/SharedStyles/ShNavigation';

interface AddApplicantDialogProps {
    open: boolean;
    onClose: () => void;
    employerJobId: number;
}

export const AddApplicantDialog = ({ open, onClose, employerJobId }: AddApplicantDialogProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { createApplicantStatus, createApplicantResponse } = useSelector((state: RootState) => state.employer.applicants.applicantActions);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        resume: null as File | null
    });
    const [emailApplicantToComplateApplication, setEmailApplicantToComplateApplication] = useState(false);
    const [showStepper, setShowStepper] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const stepLabels = ['Creating Applicant', 'Analyzing Resume', 'Calculating Resume Score'];
    const stepTimers = [3000, 6000, 9000]; // ms for each step
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (showStepper && createApplicantStatus === 'pending') {
            if (stepIndex < stepLabels.length - 1) {
                timerRef.current = setTimeout(() => {
                    setStepIndex((prev) => prev + 1);
                }, stepTimers[stepIndex]);
            }
        }
        // If API returns early, close dialog
        if (showStepper && createApplicantStatus !== 'pending') {
            setTimeout(() => {
                setShowStepper(false);
                setStepIndex(0);
                onClose();
            }, 800);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [showStepper, stepIndex, createApplicantStatus, onClose]);

    const handleSubmit = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.resume) {
            return;
        }
        setShowStepper(true);
        setStepIndex(0);
        try {
            await dispatch(createApplicant({
                ...formData,
                employerJobId,
                emailApplicantToComplateApplication,
                resume: formData.resume as File 
            }));
            // onClose will be handled by effect
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                resume: null
            });
            setEmailApplicantToComplateApplication(false);
        } catch (error) {
            console.error('Error creating applicant:', error);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setFormData(prev => ({
                ...prev,
                resume: event.target.files![0]
            }));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                {showStepper ? (
                    <Box p={4} textAlign="center">
                        <Typography variant="h6" mb={2}>Processing Applicant</Typography>
                        <Stepper activeStep={stepIndex} alternativeLabel>
                            {stepLabels.map((label, idx) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <Box mt={4} mb={2} display="flex" flexDirection="column" alignItems="center">
                            <CircularProgress color="primary" />
                            <Typography mt={2} variant="body1">{stepLabels[stepIndex]}...</Typography>
                        </Box>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            You can exit this dialog, and we'll continue processing the applicant in the background.
                        </Alert>
                        <Box mt={3}>
                            <ShButton onClick={onClose} variant="outlined">Exit</ShButton>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <ShPaper variant="outlined" headerSection>
                            <Typography variant="h6">Add a New Applicant</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Have an applicant you need to add? Simply add a few details and upload their resume. Track new applicants for this job added manually or sourced externally.
                            </Typography>
                        </ShPaper>
                        <DialogContent>
                            <Stack spacing={2}>
                                <ShTextFieldV2 label="First Name" value={formData.firstName} onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                                <ShTextFieldV2 label="Last Name" value={formData.lastName} onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                                <ShTextFieldV2 label="Email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                                <ShTextFieldV2 label="Phone Number" value={formData.phoneNumber} onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))} required fullWidth disabled={createApplicantStatus === 'pending'} />
                                <ShFileUploadButton component="label" startIcon={<UploadResumeIcon />} disabled={createApplicantStatus === 'pending'}>
                                    {formData.resume ? formData.resume.name : 'Upload Resume'}
                                    <input type="file" hidden accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFileChange} disabled={createApplicantStatus === 'pending'} />
                                </ShFileUploadButton>
                                <FormControlLabel
                                    control={
                                        <ShSwitch
                                            checked={emailApplicantToComplateApplication}
                                            onChange={(e) => setEmailApplicantToComplateApplication(e.target.checked)}
                                            disabled={createApplicantStatus === 'pending'}
                                        />
                                    }
                                    label={<Typography variant="body2" color="text.secondary" pl={2}>Email applicant to complete application</Typography>}/>
                            </Stack>
                        </DialogContent>
                        <Stack direction="row" spacing={2} justifyContent="space-between" p={1}>
                            <ShButton onClick={onClose} disabled={createApplicantStatus === 'pending'}>Cancel</ShButton>
                            <ShButton onClick={handleSubmit} variant="contained" disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.resume || createApplicantStatus === 'pending'} startIcon={createApplicantStatus === 'pending' ? <CircularProgress size={20} color="inherit" /> : null}>
                                {createApplicantStatus === 'pending' ? 'Creating...' : 'Create Applicant'}
                            </ShButton>
                        </Stack>
                    </>
                )}
            </Dialog>
            <Snackbar open={createApplicantStatus === 'success' || createApplicantStatus === 'failed'} autoHideDuration={6000} onClose={() => dispatch(resetCreateApplicant())}>
                <ShAlert severity={createApplicantStatus === 'success' ? 'success' : 'error'} onClose={() => dispatch(resetCreateApplicant())}>
                    {createApplicantResponse}
                </ShAlert>
            </Snackbar>
        </>
    );
}; 